import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { CountryTableScroll } from "../mobile/CountryTableScroll";

type View = "REGION" | "COUNTRY";

type Region = "NORTH_AMERICA" | "EUROPE" | "ASIA_PACIFIC_DEVELOPED" | "EMERGING_MARKETS" | "OTHER";

const REGION_MAP: Record<string, Region> = {
  US: "NORTH_AMERICA",
  CA: "NORTH_AMERICA",
  DE: "EUROPE",
  FR: "EUROPE",
  GB: "EUROPE",
  NL: "EUROPE",
  CH: "EUROPE",
  SE: "EUROPE",
  IT: "EUROPE",
  ES: "EUROPE",
  BE: "EUROPE",
  DK: "EUROPE",
  FI: "EUROPE",
  NO: "EUROPE",
  IE: "EUROPE",
  AT: "EUROPE",
  LU: "EUROPE",
  PT: "EUROPE",
  GR: "EUROPE",
  JP: "ASIA_PACIFIC_DEVELOPED",
  AU: "ASIA_PACIFIC_DEVELOPED",
  HK: "ASIA_PACIFIC_DEVELOPED",
  SG: "ASIA_PACIFIC_DEVELOPED",
  NZ: "ASIA_PACIFIC_DEVELOPED",
  KR: "ASIA_PACIFIC_DEVELOPED",
  IL: "ASIA_PACIFIC_DEVELOPED",
  CN: "EMERGING_MARKETS",
  IN: "EMERGING_MARKETS",
  TW: "EMERGING_MARKETS",
  BR: "EMERGING_MARKETS",
  ZA: "EMERGING_MARKETS",
  MX: "EMERGING_MARKETS",
  TR: "EMERGING_MARKETS",
  TH: "EMERGING_MARKETS",
  ID: "EMERGING_MARKETS",
  MY: "EMERGING_MARKETS",
  PH: "EMERGING_MARKETS",
  AR: "EMERGING_MARKETS",
  CL: "EMERGING_MARKETS",
  CO: "EMERGING_MARKETS",
  PL: "EMERGING_MARKETS",
  CZ: "EMERGING_MARKETS",
  HU: "EMERGING_MARKETS",
  EG: "EMERGING_MARKETS",
  AE: "EMERGING_MARKETS",
  SA: "EMERGING_MARKETS",
  QA: "EMERGING_MARKETS",
  KW: "EMERGING_MARKETS",
  RU: "EMERGING_MARKETS",
  PE: "EMERGING_MARKETS",
  PK: "EMERGING_MARKETS",
};

const REGION_COLORS: Record<Region, string> = {
  NORTH_AMERICA: "#3b82f6",
  EUROPE: "#6366f1",
  ASIA_PACIFIC_DEVELOPED: "#8b5cf6",
  EMERGING_MARKETS: "#f59e0b",
  OTHER: "#64748b",
};

const COUNTRY_FLAGS: Record<string, string> = {
  US: "🇺🇸",
  CA: "🇨🇦",
  DE: "🇩🇪",
  FR: "🇫🇷",
  GB: "🇬🇧",
  NL: "🇳🇱",
  CH: "🇨🇭",
  SE: "🇸🇪",
  IT: "🇮🇹",
  ES: "🇪🇸",
  BE: "🇧🇪",
  DK: "🇩🇰",
  FI: "🇫🇮",
  NO: "🇳🇴",
  IE: "🇮🇪",
  AT: "🇦🇹",
  LU: "🇱🇺",
  PT: "🇵🇹",
  GR: "🇬🇷",
  JP: "🇯🇵",
  AU: "🇦🇺",
  HK: "🇭🇰",
  SG: "🇸🇬",
  NZ: "🇳🇿",
  KR: "🇰🇷",
  IL: "🇮🇱",
  CN: "🇨🇳",
  IN: "🇮🇳",
  TW: "🇹🇼",
  BR: "🇧🇷",
  ZA: "🇿🇦",
  MX: "🇲🇽",
  TR: "🇹🇷",
  TH: "🇹🇭",
  ID: "🇮🇩",
  MY: "🇲🇾",
  PH: "🇵🇭",
  AR: "🇦🇷",
  CL: "🇨🇱",
  CO: "🇨🇴",
  PL: "🇵🇱",
  CZ: "🇨🇿",
  HU: "🇭🇺",
  EG: "🇪🇬",
  AE: "🇦🇪",
  SA: "🇸🇦",
  QA: "🇶🇦",
  KW: "🇰🇼",
  RU: "🇷🇺",
};

const NF_EUR = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
function fmtEur(cents: number): string {
  return NF_EUR.format(cents / 100);
}

export function CountryAllocationSection({
  perCountry,
  coveredCents,
  uncoveredCents,
}: {
  perCountry: { country: string; valueCents: number }[];
  coveredCents: number;
  uncoveredCents: number;
}) {
  const { t } = useTranslation();
  const [view, setView] = useState<View>("REGION");

  const isEmpty = perCountry.length === 0;

  const regionData = useMemo(() => {
    const acc = new Map<Region, number>();
    for (const r of perCountry) {
      const region = REGION_MAP[r.country] ?? "OTHER";
      acc.set(region, (acc.get(region) ?? 0) + r.valueCents);
    }
    return [...acc.entries()]
      .map(([region, valueCents]) => ({
        key: region,
        label: regionLabel(region, t),
        valueCents,
        color: REGION_COLORS[region],
      }))
      .sort((a, b) => b.valueCents - a.valueCents);
  }, [perCountry, t]);

  const countryData = useMemo(
    () =>
      [...perCountry]
        .sort((a, b) => b.valueCents - a.valueCents)
        .map((r) => ({
          key: r.country,
          label: countryLabel(r.country, t),
          valueCents: r.valueCents,
          flag: COUNTRY_FLAGS[r.country] ?? "🌐",
          color: REGION_COLORS[REGION_MAP[r.country] ?? "OTHER"],
        })),
    [perCountry, t],
  );

  return (
    <div className="fv-card overflow-hidden">
      <div
        className="px-6 py-4 flex items-center justify-between border-b flex-wrap gap-3"
        style={{ borderColor: "var(--border)" }}
      >
        <div>
          <h2 className="text-lg font-semibold">
            {t("allocation.country_title", { defaultValue: "Länderverteilung" })}{" "}
            <span className="text-sm font-normal" style={{ color: "var(--muted)" }}>
              {t("allocation.country_subtitle", { defaultValue: "Aktien & ETFs" })}
            </span>
          </h2>
          <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
            {fmtEur(coveredCents)} {t("allocation.country_covered", { defaultValue: "erfasst" })}
            {uncoveredCents > 0 && (
              <>
                {" · "}
                <span style={{ color: "var(--warn)" }}>
                  {fmtEur(uncoveredCents)}{" "}
                  {t("allocation.country_uncovered", {
                    defaultValue: "noch ohne Look-Through",
                  })}
                </span>
              </>
            )}
          </div>
        </div>
        <div
          className="flex gap-0.5 p-0.5 rounded-md"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <ToggleChip
            active={view === "REGION"}
            onClick={() => setView("REGION")}
            label={t("allocation.country_regions", { defaultValue: "Regionen" })}
          />
          <ToggleChip
            active={view === "COUNTRY"}
            onClick={() => setView("COUNTRY")}
            label={t("allocation.country_countries", { defaultValue: "Länder" })}
          />
        </div>
      </div>

      {isEmpty ? (
        <div className="p-6">
          <div
            className="rounded-lg p-5 text-sm"
            style={{ background: "var(--border-soft)", color: "var(--muted)" }}
          >
            <p className="mb-2">
              <b style={{ color: "var(--text)" }}>
                {t("allocation.country_empty_heading", {
                  defaultValue: "Noch keine Look-Through-Daten erfasst",
                })}
              </b>
            </p>
            <p>
              {t("allocation.country_empty_body", {
                defaultValue:
                  "Öffne ein ETF- oder Aktien-Investment, klicke „Auto-Lookup“ oder pflege die Länder manuell. Sobald Daten da sind, erscheint hier eine Regionen- und Länder-Sicht.",
              })}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-12 gap-6 p-6">
          <div className="col-span-12 md:col-span-4 flex flex-col items-center justify-center">
            <div style={{ position: "relative", width: 240, height: 240 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={view === "REGION" ? regionData : countryData}
                    dataKey="valueCents"
                    innerRadius={70}
                    outerRadius={108}
                    paddingAngle={2}
                    startAngle={90}
                    endAngle={-270}
                    isAnimationActive={false}
                  >
                    {(view === "REGION" ? regionData : countryData).map((entry) => (
                      <Cell
                        key={entry.key}
                        fill={entry.color}
                        stroke="var(--card)"
                        strokeWidth={2}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="fv-kpi-label">
                  {view === "REGION"
                    ? t("allocation.country_regions", { defaultValue: "Regionen" })
                    : t("allocation.country_countries", { defaultValue: "Länder" })}
                </div>
                <div className="text-xl font-bold tracking-tight num">{fmtEur(coveredCents)}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
                  {view === "REGION"
                    ? `${regionData.length} ${t("allocation.country_regions_short", { defaultValue: "Regionen" })}`
                    : `${countryData.length} ${t("allocation.country_countries_short", { defaultValue: "Länder" })}`}
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-8">
            <CountryTableScroll>
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="text-left text-xs"
                    style={{ background: "var(--border-soft)", color: "var(--muted)" }}
                  >
                    <th
                      className="px-3 py-2 font-semibold sticky left-0 z-10"
                      style={{ background: "var(--border-soft)" }}
                    >
                      {view === "REGION"
                        ? t("allocation.country_col_region", { defaultValue: "Region" })
                        : t("allocation.country_col_country", { defaultValue: "Land" })}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      {t("allocation.country_col_value", { defaultValue: "Wert (EUR)" })}
                    </th>
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      {t("allocation.country_col_share", { defaultValue: "Anteil" })}
                    </th>
                    <th className="px-3 py-2" style={{ width: "40%", paddingLeft: 20 }}>
                      {t("allocation.country_col_distribution", {
                        defaultValue: "Verteilung",
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(view === "REGION" ? regionData : countryData).map((row) => {
                    const pct = coveredCents > 0 ? (row.valueCents / coveredCents) * 100 : 0;
                    return (
                      <tr
                        key={row.key}
                        className="border-b"
                        style={{ borderColor: "var(--border-soft)" }}
                      >
                        <td
                          className="px-3 py-2.5 sticky left-0 z-10"
                          style={{ background: "var(--card)" }}
                        >
                          <div className="flex items-center gap-2 whitespace-nowrap">
                            {"flag" in row && <span style={{ fontSize: 14 }}>{row.flag}</span>}
                            <span
                              className="w-2.5 h-2.5 rounded-sm shrink-0"
                              style={{ background: row.color }}
                            />
                            <span className="font-medium">{row.label}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-right num font-medium whitespace-nowrap">
                          {fmtEur(row.valueCents)}
                        </td>
                        <td className="px-3 py-2.5 text-right num whitespace-nowrap">
                          {pct.toFixed(1)} %
                        </td>
                        <td className="px-3 py-2.5" style={{ minWidth: 120 }}>
                          <div
                            className="h-1.5 rounded-full overflow-hidden"
                            style={{ background: "var(--border-soft)" }}
                          >
                            <div
                              style={{
                                width: `${Math.min(100, pct).toFixed(2)}%`,
                                height: "100%",
                                background: row.color,
                              }}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CountryTableScroll>
            <div
              className="mt-4 pt-4 border-t text-xs flex flex-wrap gap-3"
              style={{ borderColor: "var(--border)", color: "var(--muted)" }}
            >
              <span>
                <b style={{ color: "var(--text)" }}>
                  {t("allocation.country_method_label", { defaultValue: "Methodik" })}:
                </b>{" "}
                {t("allocation.country_method", {
                  defaultValue:
                    "Look-Through über ETF-Holdings (Seeds + manuelle Overrides), Einzelaktien per ISIN-Land.",
                })}
              </span>
              <span className="ml-auto">
                <b style={{ color: "var(--text)" }}>
                  {t("allocation.country_excluded_label", {
                    defaultValue: "Ausgeschlossen",
                  })}
                  :
                </b>{" "}
                {t("allocation.country_placeholder_excluded", {
                  defaultValue: "Krypto · P2P · Rohstoffe · Liquide · Immobilien",
                })}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ToggleChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-3 py-1 rounded text-xs font-medium transition-colors"
      style={
        active
          ? { background: "var(--text)", color: "var(--bg)" }
          : { color: "var(--muted)", background: "transparent" }
      }
    >
      {label}
    </button>
  );
}

function regionLabel(
  region: Region,
  t: (k: string, opt?: { defaultValue?: string }) => string,
): string {
  switch (region) {
    case "NORTH_AMERICA":
      return t("allocation.region.north_america", { defaultValue: "Nordamerika" });
    case "EUROPE":
      return t("allocation.region.europe", { defaultValue: "Europa" });
    case "ASIA_PACIFIC_DEVELOPED":
      return t("allocation.region.asia_pacific", {
        defaultValue: "Asien-Pazifik (entw.)",
      });
    case "EMERGING_MARKETS":
      return t("allocation.region.emerging_markets", {
        defaultValue: "Emerging Markets",
      });
    case "OTHER":
      return t("allocation.region.other", { defaultValue: "Sonstige" });
  }
}

function countryLabel(
  code: string,
  t: (k: string, opt?: { defaultValue?: string }) => string,
): string {
  return t(`allocation.country.${code}`, { defaultValue: code });
}
